import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';
import { useDecideGlossaryProposal, useGlossaryProposal } from '@/lib/api/knowledge-base';

import { ProposalStatusBadge } from './kb-badges';

// The page the approver's Slack DM links to. Loading it changes nothing; only the buttons
// decide, since Slack fetches every link it unfurls.
export function GlossaryProposalPage({ proposalId }: { proposalId: string }) {
  const proposalQuery = useGlossaryProposal(proposalId);
  const approve = useDecideGlossaryProposal('approve');
  const reject = useDecideGlossaryProposal('reject');
  const [error, setError] = useState<string | null>(null);

  const proposal = proposalQuery.data;
  if (proposalQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (proposalQuery.isError || !proposal) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load the proposal: {proposalQuery.error?.message ?? 'not found'}
      </p>
    );
  }

  const busy = approve.isPending || reject.isPending;
  const decide = (mutate: typeof approve.mutate, fallback: string) => {
    setError(null);
    mutate(proposalId, {
      onError: (caught) => setError(caught instanceof ApiError ? caught.message : fallback),
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Link
          to="/knowledge-base/glossary-proposals"
          className="text-sm text-[var(--color-muted-foreground)] hover:underline"
        >
          ← Glossary proposals
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          {proposal.en} → {proposal.ko}
        </h1>
        <div className="mt-1 flex items-center gap-2">
          <ProposalStatusBadge status={proposal.status} />
          <span className="text-sm text-[var(--color-muted-foreground)]">
            Proposed {new Date(proposal.createdAt).toLocaleString()}
          </span>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 pt-6 text-sm">
          <Field label="English">{proposal.en}</Field>
          <Field label="Korean">{proposal.ko}</Field>
          <Field label="Gloss">{proposal.gloss}</Field>
          <Field label="Where used">{proposal.whereUsed ?? '—'}</Field>
          <Field label="Came from">
            {proposal.sourceUrl ? (
              <a href={proposal.sourceUrl} target="_blank" rel="noreferrer" className="underline">
                The Slack message
              </a>
            ) : (
              '—'
            )}
            {proposal.sessionId && (
              <>
                {' · '}
                <Link
                  to="/sessions/$sessionId"
                  params={{ sessionId: proposal.sessionId }}
                  className="underline"
                >
                  The translator's session
                </Link>
              </>
            )}
          </Field>
          {proposal.decidedAt && (
            <Field label="Decided">
              {proposal.status} by {proposal.decidedByEmail ?? 'a removed user'} on{' '}
              {new Date(proposal.decidedAt).toLocaleString()}
            </Field>
          )}
        </CardContent>
      </Card>

      {proposal.status === 'pending' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              type="button"
              disabled={busy}
              onClick={() => decide(approve.mutate, 'Could not approve the proposal.')}
            >
              Approve and add to glossary
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => decide(reject.mutate, 'Could not reject the proposal.')}
            >
              Reject
            </Button>
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Approving appends the row to <code>glossary/en-kr-terms</code> and keeps it approved, so
            the translator uses the term from its next thread.
          </p>
        </div>
      )}
      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2">
      <span className="text-[var(--color-muted-foreground)]">{label}</span>
      <span>{children}</span>
    </div>
  );
}
